import {
  PerformanceOverTimeMetricCards,
  MetricSectionTitle,
  StyledPrimarySubtitle,
  StyledSecondarySubtitle,
  SubtitleContainer,
  StyledCaption,
  ChangeMetricSpacingSubtitleContainer,
  StyledDivider,
  PerformanceOverTimeMediumLink,
} from 'modules/intelligentInsights/frequencyOptimization/components/performanceOverTime/PerformanceOverTime.styles';
import {
  AVERAGE_SENDS_ACROSS_CHANNELS_TOOLTIP,
  AVERAGE_SENDS_PER_MEDIUM,
  BIGGEST_CHANGE,
  BIGGEST_LIFT,
  BREAKDOWN_AUDIENCE_AVERAGE_SENDS_WITH_HOLDOUT,
  FRO_BREAKDOWN_ANCHOR,
  MESSAGES_PER_WEEK,
} from 'modules/intelligentInsights/constants';
import { Help16 } from 'ui/icons';
import { Tooltip } from 'ui/tooltip';
import { useFroInsightsCoreContext } from 'modules/intelligentInsights/frequencyOptimization/contexts/CoreContext';
import {
  FroMessageMediums,
  MessageMediumMetrics,
  MessageMediumTabs,
} from 'modules/intelligentInsights/frequencyOptimization/types';
import {
  formatAvgSendsDiff,
  customLocalizeNumber,
  truncateNumber,
} from 'modules/intelligentInsights/domain.utils';
import {
  getBiggestChangeText,
  getBiggestLiftText,
  shouldShowValue,
} from 'modules/intelligentInsights/frequencyOptimization/components/performanceOverTime/utils';
import { MORE_ABOUT_THIS_CHANGE } from 'modules/intelligentInsights/constants';
import { localizePercent } from 'utils/l10n';

export const sumOptimizedSends = (
  messageMediumData: MessageMediumMetrics[],
  dataKey: 'weeklyOptimizedSendsPerUser' | 'weeklyHoldoutSendsPerUser',
): number => {
  return messageMediumData.reduce((total, item) => {
    return total + (item[dataKey] ?? 0);
  }, 0);
};

export const PERFORMANCE_OVER_TIME_METRIC_CARDS_TEST_ID =
  'performance-over-time-metric-cards-section';
export const PERFORMANCE_OVER_TIME_METRIC_LIFT = 'performance-over-time-metric-lift';
export const PERFORMANCE_OVER_TIME_METRIC_CHANGE = 'performance-over-time-metric-change';
export const BIGGEST_LIFT_LINK = 'biggest-lift-link';
export const BIGGEST_CHANGE_LINK = 'biggest-change-link';

export const PeformanceOverTimeMetricCards = () => {
  const { froInsightsData, setSelectedMedium } = useFroInsightsCoreContext();
  const { messageMediumData, cardData } = froInsightsData;
  const optimizedSendCountsAcrossMediums = sumOptimizedSends(
    messageMediumData,
    'weeklyOptimizedSendsPerUser',
  );
  const holdoutSendCountsAcrossMediums = sumOptimizedSends(
    messageMediumData,
    'weeklyHoldoutSendsPerUser',
  );
  const toDisplay = formatAvgSendsDiff(
    holdoutSendCountsAcrossMediums,
    optimizedSendCountsAcrossMediums,
  );

  const liftValue = truncateNumber(cardData.largestLiftValue, 4);
  const shouldDisplayLift = shouldShowValue(liftValue);
  const liftDisplay = shouldDisplayLift ? localizePercent(liftValue) : null;
  const biggestLiftText = getBiggestLiftText(
    cardData.largestLiftValue,
    cardData.largestLiftMedium,
    cardData.largestLiftMetric,
  );
  const changedValue = truncateNumber(cardData.largestChangeValue);
  const shouldDisplayChanged = shouldShowValue(changedValue);
  const changedDisplay = shouldDisplayChanged ? `${changedValue}% ` : null;
  const biggestChangeText = getBiggestChangeText(
    cardData.largestChangeValue,
    cardData.largestChangeMedium,
    cardData.largestChangeMetric,
  );

  const navigateToMessageMedium = (medium: FroMessageMediums) => {
    setSelectedMedium(MessageMediumTabs[medium]);
    const element = document.getElementById(FRO_BREAKDOWN_ANCHOR);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <PerformanceOverTimeMetricCards data-test={PERFORMANCE_OVER_TIME_METRIC_CARDS_TEST_ID}>
      <section>
        <MetricSectionTitle>
          {AVERAGE_SENDS_PER_MEDIUM}
          <Tooltip tooltipContent={AVERAGE_SENDS_ACROSS_CHANNELS_TOOLTIP}>
            <Help16 />
          </Tooltip>
        </MetricSectionTitle>
        <SubtitleContainer>
          <StyledPrimarySubtitle>
            {customLocalizeNumber(optimizedSendCountsAcrossMediums)}
          </StyledPrimarySubtitle>
          <StyledSecondarySubtitle>{MESSAGES_PER_WEEK}</StyledSecondarySubtitle>
        </SubtitleContainer>
        <StyledCaption>
          {BREAKDOWN_AUDIENCE_AVERAGE_SENDS_WITH_HOLDOUT.PRENUMBER} {toDisplay}{' '}
          {BREAKDOWN_AUDIENCE_AVERAGE_SENDS_WITH_HOLDOUT.POSTNUMBER}
        </StyledCaption>
      </section>
      <StyledDivider />
      <section data-test={PERFORMANCE_OVER_TIME_METRIC_LIFT}>
        <MetricSectionTitle>{BIGGEST_LIFT}</MetricSectionTitle>
        <ChangeMetricSpacingSubtitleContainer>
          {shouldDisplayLift && <StyledPrimarySubtitle>{liftDisplay}</StyledPrimarySubtitle>}
          <StyledSecondarySubtitle>{biggestLiftText}</StyledSecondarySubtitle>
        </ChangeMetricSpacingSubtitleContainer>
        <PerformanceOverTimeMediumLink
          data-test={BIGGEST_LIFT_LINK}
          tabIndex={0}
          role="link"
          aria-label="Message Medium Link"
          onClick={() => {
            navigateToMessageMedium(cardData.largestLiftMedium);
          }}
        >
          {MORE_ABOUT_THIS_CHANGE}
        </PerformanceOverTimeMediumLink>
      </section>
      <StyledDivider />
      <section data-test={PERFORMANCE_OVER_TIME_METRIC_CHANGE}>
        <MetricSectionTitle>{BIGGEST_CHANGE}</MetricSectionTitle>
        <ChangeMetricSpacingSubtitleContainer>
          {shouldDisplayChanged && <StyledPrimarySubtitle>{changedDisplay}</StyledPrimarySubtitle>}
          <StyledSecondarySubtitle>{biggestChangeText}</StyledSecondarySubtitle>
        </ChangeMetricSpacingSubtitleContainer>
        <PerformanceOverTimeMediumLink
          data-test={BIGGEST_CHANGE_LINK}
          tabIndex={0}
          role="link"
          aria-label="Message Medium Link"
          onClick={() => {
            navigateToMessageMedium(cardData.largestChangeMedium);
          }}
        >
          {MORE_ABOUT_THIS_CHANGE}
        </PerformanceOverTimeMediumLink>
      </section>
    </PerformanceOverTimeMetricCards>
  );
};
